import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  View,
  ScrollView,
  Text,
  SafeAreaView,
} from 'react-native';

import { SITE_ROOT } from '../../../config';
import ThemedButton from '../../ui/Button';
import { HeaderText } from '../../../modules/accessbilityRoles';
import { addQueryStringParams } from '../../../modules/url';
import MajorLink from './MajorLink';
import TrackedLink from './TrackedLink';
import { SearchPopoverModal } from '../../consumer/search/SearchPopover';
import {
  CLIENT_APPTS_LIST,
  HOME_ROUTE,
  PRO_TODAY_TAB_HOME,
} from '../../../route-names';

import './Sidebar.module.scss';
import { ABTestMenuModal } from '../developer/ABTestMenu';

const BusinessImage = require('../../../../images/icons/icon-open-sign.svg');
const CalendarImage = require('../../../../images/icons/icon-calendar-solid.svg');
const FacebookImage = require('../../../../images/icons/icon-facebook.svg');
const HomeImage = require('../../../../images/icons/icon-home.svg');
const InstagramImage = require('../../../../images/icons/icon-instagram.svg');
const LionImage = require('../../../../images/icons/styleseat-lion-black.svg');
const MyAccountImage = require('../../../../images/icons/icon-user.svg');
const PinterestImage = require('../../../../images/icons/icon-pinterest.svg');
const SearchImage = require('../../../../images/icons/icon-thin-search.svg');
const TwitterImage = require('../../../../images/icons/icon-twitter.svg');
const CircleStarImage = require('../../../../images/icons/circle-star.svg');

// the `isStandalonePage` page prop is only true when we're rendering under the client layout test
// on the `consumer.more` route
const SidebarWrapper = ({ children, isStandalonePage }) => (
  <SafeAreaView
    styleName="sidebar"
    style={{
      maxWidth: isStandalonePage ? '100%' : undefined,
    }}
    testID={`Sidebar${isStandalonePage ? '-standalone' : ''}`}
  >
    {children}
  </SafeAreaView>
);

SidebarWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  isStandalonePage: PropTypes.bool.isRequired,
};

/**
 * Displays the mobile sidebar
 */
const DeprecatedSidebar = ({
  isStandalonePage,
  isSignupAllowed,
  isLoggedIn,
  isProvider,
  isSMSSupportEnabled,
  linkTo,
  onBlogClick,
  onClose,
  onLogin,
  onProviderSignup,
  onSMSSupportClick,
  onSignup,
  onSocialClick,
  openPrivacy,
  openTerms,
  onOpenSupport,
  providerId,
  searchQuery,
  onOpenClientSplash,
  utmParams,
  onSearchPopoverSubmit,
  showSearchModal,
  onToggleSearchModal,
}) => {
  const [isDeveloperMenuOpen, setDeveloperMenuOpen] = useState(false);

  return (
    <SidebarWrapper isStandalonePage={isStandalonePage}>
      <ScrollView>
        {!isLoggedIn && (
          <View styleName="guest-actions" className="client-app">
            <ThemedButton appearance="primary" onClick={onSignup}>
              Sign Up
            </ThemedButton>
            <ThemedButton appearance="primary" onClick={onLogin}>
              Log In
            </ThemedButton>
          </View>
        )}
        <View>
          {isProvider ? (
            <MajorLink
              testID="sidebar-major-link-my-business"
              linkTo={linkTo}
              trackingKey="more"
              route={PRO_TODAY_TAB_HOME}
              params={{ providerId, ...utmParams }}
              image={CircleStarImage}
              imageStyle={{
                height: 30,
                width: 30,
              }}
            >
              My Business
            </MajorLink>
          ) : null}
          <MajorLink
            testID="sidebar-major-link-home"
            linkTo={linkTo}
            trackingKey="home"
            route={HOME_ROUTE}
            params={{ index: null, ...utmParams }}
            image={HomeImage}
            onPress={onClose}
          >
            Home
          </MajorLink>
          <MajorLink
            testID="sidebar-major-link-search"
            onPress={() => onToggleSearchModal(true)}
            image={SearchImage}
            trackingKey="search"
          >
            Search
          </MajorLink>
          {isLoggedIn ? (
            <React.Fragment>
              <MajorLink
                testID="sidebar-major-link-my-appointments"
                linkTo={linkTo}
                trackingKey="my_appointments"
                route={CLIENT_APPTS_LIST}
                params={utmParams}
                onPress={onClose}
                image={CalendarImage}
              >
                My Appointments
              </MajorLink>
              <MajorLink
                testID="sidebar-major-link-my-account"
                linkTo={linkTo}
                trackingKey="my_account"
                route="consumer.account"
                params={utmParams}
                onPress={onClose}
                image={MyAccountImage}
              >
                My Account
              </MajorLink>
            </React.Fragment>
          ) : null}
          <MajorLink
            testID="sidebar-major-link-blog"
            image={LionImage}
            imageHeight={28}
            onPress={onBlogClick}
            trackingKey="client_blog"
            params={utmParams}
          >
            Blog
          </MajorLink>
          {isProvider || !isSignupAllowed ? null : (
            <MajorLink
              linkTo={linkTo}
              trackingKey="list_your_business"
              onPress={onProviderSignup}
              image={BusinessImage}
              params={utmParams}
              testID="sidebar-pro-cta"
            >
              Set up my business
            </MajorLink>
          )}
        </View>
        <View>
          {!isLoggedIn && isSignupAllowed ? (
            <TrackedLink
              testID="sidebar-tracked-link-list-business"
              target="_system"
              styleName="minor-link"
              trackingKey="how_to_list_your_business"
              href={addQueryStringParams(`${SITE_ROOT}/join`, { source: 'hamburger_menu', ...utmParams })}
            >
              How To List Your Business
            </TrackedLink>
          ) : null}
          {!isLoggedIn ? (
            <TrackedLink
              testID="sidebar-tracked-link-book-appointment"
              target="_system"
              styleName="minor-link"
              trackingKey="how_to_book_an_appointment"
              onPress={onOpenClientSplash}
            >
              How To Book An Appointment
            </TrackedLink>
          ) : null}
          {isSignupAllowed ? (
            <TrackedLink
              testID="sidebar-tracked-link-help"
              styleName="minor-link"
              trackingKey="help"
              onPress={onOpenSupport}
              onSecretClick={() => setDeveloperMenuOpen(true)}
            >
              Help Center
            </TrackedLink>
          ) : null}
          {isSMSSupportEnabled ? (
            <TrackedLink
              testID="sidebar-tracked-link-text-us"
              styleName="minor-link"
              trackingKey="sms"
              onPress={onSMSSupportClick}
            >
              Text Us
            </TrackedLink>
          ) : null}
          <TrackedLink
            testID="sidebar-tracked-link-careers"
            styleName="minor-link"
            trackingKey="careers"
            href={addQueryStringParams(`${SITE_ROOT}/careers/`, utmParams)}
            target="_system"
          >
            Careers
          </TrackedLink>
          <TrackedLink
            testID="sidebar-tracked-link-terms"
            styleName="minor-link"
            trackingKey="terms"
            onPress={openTerms}
          >
            Terms
          </TrackedLink>
          <TrackedLink
            testID="sidebar-tracked-link-privacy"
            styleName="minor-link"
            trackingKey="privacy"
            onPress={openPrivacy}
          >
            Privacy
          </TrackedLink>
          <TrackedLink
            testID="sidebar-tracked-link-sitemap"
            styleName="minor-link"
            trackingKey="sitemap"
            href="https://www.styleseat.com/sitemap"
          >
            Sitemap
          </TrackedLink>
        </View>
        <View styleName="sidebar-social">
          <Text accessibilityRole={HeaderText} aria-level="2" styleName="social-header">
            Follow us:
          </Text>
          <View styleName="social-links">
            <TrackedLink
              testID="sidebar-tracked-link-facebook"
              target="_blank"
              trackingKey="follow_FB"
              href="https://www.facebook.com/484439085178"
              title="Facebook"
              onPress={e => onSocialClick(e, 'facebook')}
              image={FacebookImage}
              imageStyle={{
                height: 30,
                width: 30,
              }}
            />
            <TrackedLink
              testID="sidebar-tracked-link-instagram"
              target="_blank"
              trackingKey="follow_instagram"
              href="https://instagram.com/styleseat/"
              title="Instagram"
              onPress={e => onSocialClick(e, 'instagram')}
              image={InstagramImage}
              imageStyle={{
                height: 30,
                width: 30,
              }}
            />
            <TrackedLink
              testID="sidebar-tracked-link-pinterest"
              target="_blank"
              trackingKey="follow_pinterest"
              href="https://www.pinterest.com/styleseat/"
              title="Pinterest"
              onPress={e => onSocialClick(e, 'pinterest')}
              image={PinterestImage}
              imageStyle={{
                height: 30,
                width: 30,
              }}
            />
            <TrackedLink
              testID="sidebar-tracked-link-twitter"
              target="_blank"
              trackingKey="follow_twitter"
              href="https://twitter.com/styleseat"
              title="Twitter"
              onPress={e => onSocialClick(e, 'twitter')}
              image={TwitterImage}
              imageStyle={{
                height: 30,
                width: 30,
              }}
            />
          </View>
        </View>
      </ScrollView>
      {showSearchModal && (
        <SearchPopoverModal
          query={searchQuery}
          source="sidebar_search_entry"
          onClose={() => onToggleSearchModal(false)}
          onSubmit={onSearchPopoverSubmit}
        />
      )}
      {isDeveloperMenuOpen && (
        <ABTestMenuModal
          onClose={() => setDeveloperMenuOpen(false)}
        />
      )}
    </SidebarWrapper>
  );
};

DeprecatedSidebar.propTypes = {
  /** True to render the sidebar for standalone display, otherwise false */
  isStandalonePage: PropTypes.bool,
  /** True if the current user is a provider */
  isProvider: PropTypes.bool,
  /** True if the current user is logged in  */
  isLoggedIn: PropTypes.bool,
  /** True if direct pro signup is allowed */
  isSignupAllowed: PropTypes.bool,
  /** True if SMS support is enabled */
  isSMSSupportEnabled: PropTypes.bool,
  /** The default search query for navigation */
  searchQuery: PropTypes.string,
  /** The ID of the logged in provider */
  providerId: PropTypes.number,
  /** Called when user requests to open terms of service */
  openTerms: PropTypes.func,
  /** Called when user requests to open privacy policy */
  openPrivacy: PropTypes.func,
  /** Called when user clicks SMS support link */
  onSMSSupportClick: PropTypes.func,
  /** Called when the sidebar should be closed */
  onClose: PropTypes.func,
  /** Called when the user clicks on the provider signup link */
  onProviderSignup: PropTypes.func,
  /** Called when the user clicks on a social link, supplied with the `SyntheticEvent` and the
   * social network name */
  onSocialClick: PropTypes.func,
  /** Called to perform navigation, provided with a route and a parameter object */
  linkTo: PropTypes.func,
  /** Called when user wishes to open the support page */
  onOpenSupport: PropTypes.func,
  /** Called when user requests to log in */
  onLogin: PropTypes.func,
  /** Called when user requests to sign up */
  onSignup: PropTypes.func,
  /** Called when the user clicks on the blog link */
  onBlogClick: PropTypes.func,
  /** Called when user wishes to learn more about how to book */
  onOpenClientSplash: PropTypes.func,
  /** Object of utms */
  utmParams: PropTypes.object,
  /** Determines if search modal is showing */
  showSearchModal: PropTypes.bool,
  /** Routes to search page on submit */
  onSearchPopoverSubmit: PropTypes.func.isRequired,
  /** Sets open/close state for search modal */
  onToggleSearchModal: PropTypes.func.isRequired,
};

DeprecatedSidebar.defaultProps = {
  isStandalonePage: false,
  isSignupAllowed: false,
  isLoggedIn: false,
  isProvider: false,
  isSMSSupportEnabled: false,
  linkTo: null,
  onBlogClick: null,
  onClose: null,
  onLogin: null,
  onProviderSignup: null,
  onSMSSupportClick: null,
  onSignup: null,
  onSocialClick: null,
  openPrivacy: null,
  openTerms: null,
  onOpenSupport: null,
  providerId: 0,
  searchQuery: '',
  onOpenClientSplash: null,
  utmParams: {},
  showSearchModal: false,
};

export default DeprecatedSidebar;
